import React, { useEffect, useState } from 'react';
import {
    Autosuggest,
    Button,
    Box,
    ColumnLayout,
    Container,
    ExpandableSection,
    FormField,
    Grid,
    Header,
    Icon,
    Pagination,
    SpaceBetween,
    Popover,
    Spinner,
    Table,
} from '@amzn/awsui-components-react';
import TPPieChart from '../../shared/TPPieChart';
import { MARReportSummary } from 'src/models/reports/ReportGenerationSummary';
import { getReasonCodeDescription, getDisplayReasonCode, CREATE_TICKET_URL } from './util';

type ComponentProps = {
    summary?: MARReportSummary;
    loading?: boolean;
    tpTaxOwner?: string;
    tpTaxOwnerOptions?: string[];
    onTpTaxOwnerChanged?: (value: string) => void;
    onApplyFilter?: () => void;
    onClearFilter?: () => void;

}

export default function MARReportSummarySection({ summary, loading, tpTaxOwner, tpTaxOwnerOptions, onTpTaxOwnerChanged, onApplyFilter, onClearFilter }: ComponentProps) {
    const PAGE_SIZE = 20;
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
    }, [summary]);

    if (!summary) {
        return (
            <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
                <Container header={<Header variant="h2" description="CLI-level report generation status for Monthly Accounting Reports">MAR Report Status</Header>}>
                    {loading ? (
                        <Box textAlign="center" padding={{ top: "l", bottom: "l" }}><Spinner size="large" /><Box variant="p" color="inherit" padding={{ top: "s" }}>Loading MAR data...</Box></Box>
                    ) : (
                        <Box textAlign="center" padding={{ top: "l", bottom: "l" }} color="inherit"><b>No data available</b><Box variant="p" color="inherit">No MAR report status data available to display</Box></Box>
                    )}
                </Container>
                <Box textAlign="center" padding={{ top: "xxl", bottom: "l" }} color="inherit">
                    {loading ? (
                        <><Spinner size="large" /><Box variant="p" color="inherit" padding={{ top: "s" }}>Loading chart...</Box></>
                    ) : (
                        <><b>No data available</b><Box variant="p" color="inherit">No MAR chart data available</Box></>
                    )}
                </Box>
            </Grid>
        );
    }

    const calcPercentage = (count: number) =>
        summary.totalExpected ? (count / summary.totalExpected) * 100 : 0;


    const pieChartData = [
        { title: 'Success', percentage: calcPercentage(summary.successCount), value: summary.successCount },
        { title: 'Failed', percentage: calcPercentage(summary.failureCount), value: summary.failureCount },
        { title: 'In-Progress', percentage: calcPercentage(summary.pendingCount), value: summary.pendingCount },
    ].filter(slice => slice.value > 0);


    return (
        <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
            <Container header={
                <Header variant="h2"
                    description="CLI-level report generation status for Monthly Accounting Reports"
                    actions={onTpTaxOwnerChanged && (
                        <SpaceBetween direction="horizontal" size="xs">
                            <FormField label="TP Tax Owner">
                                <Autosuggest
                                    value={tpTaxOwner || ''}
                                    onChange={({ detail }) => onTpTaxOwnerChanged(detail.value)}
                                    options={(tpTaxOwnerOptions || []).map((owner) => ({ value: owner }))}
                                    enteredTextLabel={(value) => `Use: "${value}"`}
                                    placeholder="Filter by TP Tax Owner"
                                    empty="No matching TP Tax Owner"
                                />
                            </FormField>
                            <FormField label="‎">
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button onClick={onApplyFilter} variant="primary">Apply</Button>
                                    {tpTaxOwner && <Button onClick={onClearFilter} variant="link">Clear</Button>}
                                </SpaceBetween>
                            </FormField>
                        </SpaceBetween>
                    )}>
                    MAR Report Status
                </Header>}>
                <ColumnLayout columns={5} variant="text-grid">
                    <div>
                        <Box variant="awsui-key-label">Success</Box>
                        <Box variant="h1">{summary.successCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Failed</Box>
                        <Box variant="h1">{summary.failureCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">In-Progress</Box>
                        <Box variant="h1">{summary.pendingCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Total CLIs</Box>
                        <Box variant="h1">{summary.totalExpected}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Success Rate</Box>
                        <Box variant="h1">{calcPercentage(summary.successCount).toFixed(1)}%</Box>
                    </div>
                </ColumnLayout>

                {summary.failedCLIs.length > 0 && (
                    <ExpandableSection headerText={`Failed CLIs (${summary.failedCLIs.length})`} variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'cli', header: 'CLI', cell: (item) => item.cli },
                                { id: 'reasonCode', header: 'Reason Code', cell: (item) => (
                                    <Popover header={getDisplayReasonCode(item.reasonCode)} content={
                                        <span>
                                            {getReasonCodeDescription(item.reasonCode)} Please raise a{' '}
                                            <a href={CREATE_TICKET_URL} target="_blank" rel="noopener noreferrer">ticket</a> for investigation.
                                            <br/><b>CTI:</b> Finance Tech / FT Intercompany / IC Reporting
                                        </span>
                                    }>
                                        <Box color="text-status-info" fontSize="body-m">{getDisplayReasonCode(item.reasonCode)} <Icon name="status-info" size="small" /></Box>
                                    </Popover>
                                )},
                                { id: 'tpTaxOwner', header: 'TP Tax Owner', cell: (item) => item.tpTaxOwner || '—' },
                            ]}
                            items={summary.failedCLIs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)}
                            variant="embedded"
                            pagination={
                                summary.failedCLIs.length > PAGE_SIZE ? (
                                    <Pagination
                                        currentPageIndex={currentPage}
                                        pagesCount={Math.ceil(summary.failedCLIs.length / PAGE_SIZE)}
                                        onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                                    />
                                ) : undefined
                            }
                        />
                    </ExpandableSection>
                )}

                {summary.reasonCodeSummary && Object.keys(summary.reasonCodeSummary).length > 0 && (
                    <ExpandableSection headerText="Failure Breakdown for MAR" variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'reason', header: 'Reason', cell: (item) => item.reason },
                                { id: 'count', header: 'Count', cell: (item) => item.count },
                            ]}
                            items={Object.entries(
                                Object.entries(summary.reasonCodeSummary).reduce((acc, [code, count]) => {
                                    const display = getDisplayReasonCode(code);
                                    acc[display] = (acc[display] || 0) + (count as number);
                                    return acc;
                                }, {} as Record<string, number>)
                            ).map(([reason, count]) => ({ reason, count }))}
                            variant="embedded"
                        />
                    </ExpandableSection>
                )}

            </Container>
            <TPPieChart
                data={pieChartData}
                totalUnits={summary.totalExpected}
                ariaDescription="Donut chart showing MAR report generation status"
                ariaLabel="MAR report status donut chart"
                emptyDataDescription="No MAR report status data available"
                noMatchDataDescription="No MAR data for the selected filter criteria"
            />
        </Grid>
    );
}
